import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { getCurrentUser, fetchUserAttributes } from "aws-amplify/auth";
import { client } from "../../amplify-client";
import {
  hideConversationForUser,
} from "../../api/communication/conversation-visibility-service";
import {
  hideMessageForUser,
  listHiddenMessageSkForUserChat,
} from "../../api/communication/message-visibility-service";
import {
  publishCommunicationEvent,
  subscribeCommunicationEvents,
} from "../../api/communication/communication-events-service";
import {
  invalidateCommunicationInboxSnapshot,
  loadCommunicationInboxSnapshot,
} from "../../api/communication/communication-inbox-service";
import { watchPresenceForUsers } from "../../api/presence";

function sortAscString(a, b) {
  return String(a ?? "").localeCompare(String(b ?? ""));
}

function normalizeTimestampValue(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  const asDate = new Date(normalized);
  if (!Number.isNaN(asDate.getTime())) return asDate.toISOString();
  return normalized;
}

function isNewerTimestamp(candidate, previous) {
  const normalizedCandidate = normalizeTimestampValue(candidate);
  const normalizedPrevious = normalizeTimestampValue(previous);
  if (!normalizedCandidate) return false;
  if (!normalizedPrevious) return true;
  return normalizedCandidate > normalizedPrevious;
}

function upsertChatByActivity(chats, nextChat) {
  const normalizedChatPk = String(nextChat?.chatPk || "").trim();
  const previousChats = Array.isArray(chats) ? chats : [];
  const remainingChats = previousChats.filter(
    (chat) => String(chat?.chatPk || "").trim() !== normalizedChatPk
  );
  return [nextChat, ...remainingChats].sort((a, b) =>
    String(b?.lastUpdatedAt || "").localeCompare(String(a?.lastUpdatedAt || ""))
  );
}

// Truncated SHA-256 (50 chars) so the channel name fits AppSync Events
// API's 50-char-per-segment limit. Must match active.functions.js exactly
// so both surfaces resolve to the same channel name.
async function channelIdFromChatPk(chatPk) {
  const raw = String(chatPk || "");
  const bytes = new TextEncoder().encode(raw);
  const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
  const hex = Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hex.slice(0, 50);
}

// Member-gated single-chat fetch. Direct Chat.list/get from clients is denied
// at the model level — only chat_members can read a Chat row, enforced by the
// chatRead lambda behind getMyChat.
async function fetchChatByPk(chatPk) {
  const resp = await client.queries.getMyChat({ chatPk });
  const errors = Array.isArray(resp?.errors) ? resp.errors : [];
  if (errors.length) {
    throw new Error(errors.map((err) => err.message).join(", "));
  }
  return resp?.data ?? null;
}

// Builds the Past chat route target so list cards can navigate without duplicating slug logic.
export function buildPastChatNavigation(chat) {
  const chatName = String(chat?.title || chat?.name || "chat").trim() || "chat";
  return {
    pathname: `/past/chat/${encodeURIComponent(chatName)}`,
    state: {
      chat,
      selectedChatPk: chat?.chatPk,
    },
  };
}

// Loads and coordinates Past screen inbox state, selection, message history, and hide actions.
export function usePastData(outletContextOverride = null) {
  const navigate = useNavigate();
  const location = useLocation();
  const routeOutletContext = useOutletContext();
  const outletContext = outletContextOverride ?? routeOutletContext;
  const communicationCache = outletContext?.communicationCache;
  const shellCurrentUser = outletContext?.currentUser;
  const initialSelected = location?.state?.selectedChatPk ?? null;

  const [selectedChatPk, setSelectedChatPk] = useState(initialSelected);
  const [message, setMessage] = useState("");
  const [me, setMe] = useState(() => ({
    userId: shellCurrentUser?.userId || null,
    email: shellCurrentUser?.email || null,
    name: shellCurrentUser?.name || null,
  }));
  const [chatListLoading, setChatListLoading] = useState(true);
  const [availableChats, setAvailableChats] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const inFlightListLoadRef = useRef(false);
  const pendingListReloadRef = useRef(false);
  const scheduledReloadRef = useRef(null);
  const latestSyncedAtRef = useRef("");
  const latestAppliedActivityByChatRef = useRef(new Map());
  const recentInboxEventIdsRef = useRef(new Set());
  const selectedChatPkRef = useRef(selectedChatPk);
  useEffect(() => {
    if (initialSelected) setSelectedChatPk(initialSelected);
  }, [initialSelected]);

  useEffect(() => {
    selectedChatPkRef.current = selectedChatPk;
  }, [selectedChatPk]);

  // Live presence for past-chat counterparts, mirroring the Active provider:
  // watch transitions over AppSync Events and patch presenceStatus in place.
  // Keyed on the joined id list so presence patches don't churn
  // subscriptions (the shared watcher refcounts per user anyway).
  const presenceWatchKey = useMemo(() => {
    const ids = new Set();
    availableChats.forEach((chat) => {
      if (chat?.isGroup) return;
      const counterpartId = String(chat?.counterpartId || "").trim();
      if (counterpartId) ids.add(counterpartId);
    });
    return [...ids].sort().join("|");
  }, [availableChats]);

  useEffect(() => {
    if (!presenceWatchKey) return undefined;
    const counterpartIds = presenceWatchKey.split("|");
    return watchPresenceForUsers(counterpartIds, (userId, presence) => {
      setAvailableChats((previousChats) => {
        let changed = false;
        const nextChats = previousChats.map((chat) => {
          if (String(chat?.counterpartId || "").trim() !== userId) return chat;
          const presenceStatus = presence.online ? "online" : "offline";
          const lastSeenAt = presence.lastSeenAt || chat.lastSeenAt || null;
          if (chat.presenceStatus === presenceStatus && chat.lastSeenAt === lastSeenAt) {
            return chat;
          }
          changed = true;
          return { ...chat, presenceStatus, lastSeenAt };
        });
        return changed ? nextChats : previousChats;
      });
    });
  }, [presenceWatchKey]);

  useEffect(() => {
    let cancelled = false;
    const shellUserId = String(shellCurrentUser?.userId || "").trim();
    if (shellUserId) {
      setMe({
        userId: shellUserId,
        email: shellCurrentUser?.email || null,
        name: shellCurrentUser?.name || null,
      });
      return () => {
        cancelled = true;
      };
    }

    async function loadMe() {
      try {
        const u = await getCurrentUser();
        const attrs = await fetchUserAttributes();
        if (!cancelled) {
          setMe({
            userId: u?.userId ?? null,
            email: attrs?.email || null,
            name: attrs?.name || null,
          });
        }
      } catch (e) {
        console.error("Failed to load current user:", e);
      }
    }
    loadMe();
    return () => { cancelled = true; };
  }, [shellCurrentUser?.email, shellCurrentUser?.name, shellCurrentUser?.userId]);

  const mySenderId = me.userId;

  useEffect(() => {
    if (!mySenderId) return;
    let cancelled = false;
    const MAX_RECENT_EVENT_IDS = 300;
    const FULL_REFRESH_STALE_MS = 5 * 60 * 1000;
    latestAppliedActivityByChatRef.current = new Map();
    recentInboxEventIdsRef.current = new Set();

    function rememberRecent(setRef, value, maxSize) {
      const normalized = String(value || "").trim();
      if (!normalized || setRef.has(normalized)) return false;
      setRef.add(normalized);
      if (setRef.size > maxSize) {
        const oldest = setRef.values().next().value;
        setRef.delete(oldest);
      }
      return true;
    }

    async function loadPastChats({ silent = false, force = false } = {}) {
      if (inFlightListLoadRef.current) {
        pendingListReloadRef.current = true;
        return;
      }

      inFlightListLoadRef.current = true;
      if (!silent) setChatListLoading(true);

      try {
        const snapshot = await loadCommunicationInboxSnapshot({
          userId: mySenderId,
          force,
          maxAgeMs: silent ? 4_000 : 0,
        });
        const snapshotPastChats = Array.isArray(snapshot?.pastChats) ? snapshot.pastChats : [];
        const pastChats = snapshotPastChats;
        const activeChats = Array.isArray(snapshot?.activeChats) ? snapshot.activeChats : [];
        const latestSyncedAt = String(snapshot?.latestSyncedAt || new Date().toISOString());
        const normalizedSelectedChatPk = String(
          selectedChatPkRef.current || initialSelected || ""
        ).trim();
        const isViewingPastThread = location.pathname.startsWith("/past/chat/");
        const selectedPastChatStillVisible =
          normalizedSelectedChatPk &&
          pastChats.some((chat) => String(chat?.chatPk || "").trim() === normalizedSelectedChatPk);
        const promotedActiveChat =
          normalizedSelectedChatPk && !selectedPastChatStillVisible
            ? activeChats.find(
                (chat) => String(chat?.chatPk || "").trim() === normalizedSelectedChatPk
              ) || null
            : null;

        if (!cancelled) {
          setAvailableChats(pastChats);
          if (!initialSelected && !selectedChatPkRef.current && pastChats.length > 0) {
            setSelectedChatPk(pastChats[0].chatPk);
          }
          communicationCache?.setSnapshot?.("past", mySenderId, {
            availableChats: pastChats,
            latestSyncedAt,
          });

          if (isViewingPastThread && promotedActiveChat?.chatPk) {
            const chatName =
              String(promotedActiveChat?.title || promotedActiveChat?.name || "chat").trim() ||
              "chat";
            navigate(`/active/chat/${encodeURIComponent(chatName)}`, {
              replace: true,
              state: { selectedChatPk: promotedActiveChat.chatPk },
            });
          }
        }
        latestSyncedAtRef.current = latestSyncedAt;
      } catch (e) {
        console.error("Failed to load past chats:", e);
        if (!cancelled) {
          setAvailableChats([]);
          communicationCache?.setSnapshot?.("past", mySenderId, {
            availableChats: [],
            latestSyncedAt: new Date().toISOString(),
          });
        }
      } finally {
        inFlightListLoadRef.current = false;

        if (pendingListReloadRef.current && !cancelled) {
          pendingListReloadRef.current = false;
          void loadPastChats({ silent: true });
        }

        if (!cancelled && !silent) setChatListLoading(false);
      }
    }

    function scheduleListReload({ immediate = false } = {}) {
      if (cancelled) return;
      if (scheduledReloadRef.current) {
        clearTimeout(scheduledReloadRef.current);
      }
      const delayMs = immediate ? 0 : 120;
      scheduledReloadRef.current = setTimeout(() => {
        scheduledReloadRef.current = null;
        void loadPastChats({ silent: true });
      }, delayMs);
    }

    async function runCatchUpSync() {
      scheduleListReload({ immediate: true });
    }

    const unsubscribeInbox = subscribeCommunicationEvents((payload) => {
      const type = String(payload?.type || "");
      if (
        type !== "chat_message" &&
        type !== "chat_request_created" &&
        type !== "chat_request_updated" &&
        type !== "chat_deleted" &&
        type !== "chat_visibility_changed"
      ) {
        return;
      }

      const eventId = String(payload?.eventId || "").trim();
      if (eventId) {
        const isNew = rememberRecent(
          recentInboxEventIdsRef.current,
          eventId,
          MAX_RECENT_EVENT_IDS
        );
        if (!isNew) return;
      }

      const chatPk = String(payload?.chatPk || "").trim();
      const activityAt = String(payload?.activityAt || payload?.sentAt || "").trim();
      if (chatPk && activityAt) {
        const latestAppliedAt = latestAppliedActivityByChatRef.current.get(chatPk) || "";
        if (!isNewerTimestamp(activityAt, latestAppliedAt)) {
          return;
        }
        latestAppliedActivityByChatRef.current.set(chatPk, normalizeTimestampValue(activityAt));
      }

      const isSelectedPastThreadOpen =
        location.pathname.startsWith("/past/chat/") &&
        chatPk &&
        chatPk === String(selectedChatPkRef.current || "").trim();

      scheduleListReload({ immediate: isSelectedPastThreadOpen });
    });

    const handleFocus = () => {
      if (document.visibilityState === "hidden") return;
      void runCatchUpSync();
    };
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      void runCatchUpSync();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    const cachedSnapshot = communicationCache?.getSnapshot?.("past", mySenderId);
    const cachedPayload = cachedSnapshot?.payload;
    const cachedChats = Array.isArray(cachedPayload?.availableChats)
      ? cachedPayload.availableChats
      : [];
    const hasCachedData = cachedChats.length > 0;
    const cacheCoversInitialSelected =
      !initialSelected || cachedChats.some((chat) => chat.chatPk === initialSelected);
    const cacheUsable = hasCachedData && cacheCoversInitialSelected;

    if (cacheUsable && !cancelled) {
      setAvailableChats(cachedChats);
      setChatListLoading(false);
      setSelectedChatPk((previous) => {
        if (previous && cachedChats.some((chat) => chat.chatPk === previous)) return previous;
        if (initialSelected && cachedChats.some((chat) => chat.chatPk === initialSelected)) {
          return initialSelected;
        }
        return cachedChats[0]?.chatPk || null;
      });
      latestSyncedAtRef.current = String(
        cachedPayload?.latestSyncedAt || cachedSnapshot?.updatedAt || ""
      );
    } else {
      void loadPastChats();
    }

    const cacheUpdatedAtMs = new Date(cachedSnapshot?.updatedAt || "").getTime();
    const isCacheStale =
      !cacheUsable ||
      Number.isNaN(cacheUpdatedAtMs) ||
      Date.now() - cacheUpdatedAtMs > FULL_REFRESH_STALE_MS;

    if (cacheUsable && isCacheStale) {
      void loadPastChats({ silent: true });
    }

    if (cacheUsable) {
      void runCatchUpSync();
    }

    return () => {
      cancelled = true;
      unsubscribeInbox?.();
      if (scheduledReloadRef.current) {
        clearTimeout(scheduledReloadRef.current);
        scheduledReloadRef.current = null;
      }
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      pendingListReloadRef.current = false;
      inFlightListLoadRef.current = false;
    };
  }, [communicationCache, initialSelected, location.pathname, mySenderId, navigate]);

  useEffect(() => {
    if (!mySenderId) return;
    if (chatListLoading && !availableChats.length) return;
    communicationCache?.setSnapshot?.("past", mySenderId, {
      availableChats,
      latestSyncedAt: latestSyncedAtRef.current || new Date().toISOString(),
    });
  }, [availableChats, chatListLoading, communicationCache, mySenderId]);

  useEffect(() => {
    const isPastChatRoute = location.pathname.startsWith("/past/chat/");
    if (!isPastChatRoute || !selectedChatPk) {
      setMessages([]);
      return;
    }
    if (!mySenderId) {
      setMessagesLoading(true);
      return;
    }

    let cancelled = false;

    async function loadMessages() {
      setMessagesLoading(true);
      try {
        let nextToken = undefined;
        const rows = [];

        // Member-gated read via the listMyChatMessages custom query — direct
        // ChatMessage reads from clients are denied at the model level.
        do {
          const response = await client.queries.listMyChatMessages({
            chatPk: selectedChatPk,
            limit: 200,
            nextToken,
          });

          const errors = Array.isArray(response?.errors) ? response.errors : [];
          if (errors.length) {
            throw new Error(errors.map((error) => error.message).join(", "));
          }

          const items = Array.isArray(response?.data?.items)
            ? response.data.items
            : [];
          rows.push(...items);
          nextToken = response?.data?.nextToken ?? null;
        } while (nextToken);

        const hiddenMessageSk = await listHiddenMessageSkForUserChat({
          userId: mySenderId,
          chatPk: selectedChatPk,
        });

        const mappedMessages = rows
          .slice()
          .sort((a, b) => sortAscString(a?.sk, b?.sk))
          .filter((messageRow) => !hiddenMessageSk.has(String(messageRow?.sk || "")))
          .map((messageRow) => ({
            sender: messageRow.message_from,
            senderId: messageRow.message_from,
            text: messageRow.content,
            timestamp: messageRow.sk,
            isMe: messageRow.message_from === mySenderId,
          }));

        if (!cancelled) {
          setMessages(mappedMessages);
        }
      } catch (error) {
        console.error("Failed to load messages:", error);
        if (!cancelled) {
          setMessages([]);
        }
      } finally {
        if (!cancelled) {
          setMessagesLoading(false);
        }
      }
    }

    void loadMessages();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, mySenderId, selectedChatPk]);

  const currentChat =
    availableChats.find((chat) => String(chat?.chatPk || "") === String(selectedChatPk || "")) ||
    null;

  async function sendMessage() {
    const text = String(message || "").trim();
    const normalizedChatPk = String(selectedChatPk || "").trim();
    if (!text || !normalizedChatPk || !mySenderId) return;

    const selectedChat = currentChat;
    if (!selectedChat) return;

    let chat;
    try {
      chat = await fetchChatByPk(normalizedChatPk);
    } catch (error) {
      console.error("Failed to load past chat before publish:", error);
      return;
    }

    if (!chat) {
      console.warn(`Past chat not found (${normalizedChatPk}).`);
      return;
    }
    if (chat.status !== "accepted") {
      console.warn(
        `Past chat is not in accepted state (chatPk=${normalizedChatPk}, status=${chat.status}).`
      );
      return;
    }

    const members = Array.isArray(chat.chat_members) ? chat.chat_members : [];
    if (!members.includes(mySenderId)) {
      console.warn("You are no longer authorized for this chat.");
      return;
    }

    try {
      const activityAt = new Date().toISOString();
      const chatName = String(selectedChat?.title || selectedChat?.name || "chat").trim() || "chat";
      const clientMessageId = `past-${Date.now()}`;
      const optimisticMessage = {
        chatPk: normalizedChatPk,
        sender: me?.name || me?.email || mySenderId,
        senderId: mySenderId,
        text,
        timestamp: activityAt,
        messageId: `MESSAGE#CLIENT#${clientMessageId}`,
        localId: clientMessageId,
        isMe: true,
        isOptimistic: true,
        deliveryStatus: "sent",
      };
      const promotedChat = {
        ...selectedChat,
        status: "accepted",
        lastUpdatedAt: activityAt,
        previewText: text,
        unreadCount: 0,
      };

      const cachedActiveSnapshot = communicationCache?.getSnapshot?.("active", mySenderId);
      const cachedActivePayload = cachedActiveSnapshot?.payload;
      const cachedActiveChats = Array.isArray(cachedActivePayload?.availableChats)
        ? cachedActivePayload.availableChats
        : [];
      const cachedPendingRequests = Array.isArray(cachedActivePayload?.pendingRequests)
        ? cachedActivePayload.pendingRequests
        : [];

      const { events } = await import("aws-amplify/data");
      const channelId = await channelIdFromChatPk(normalizedChatPk);
      const channelPath = `default/chat/${channelId}`;
      setMessages((previousMessages) => [
        ...previousMessages,
        optimisticMessage,
      ]);
      setMessage("");

      let channel = null;
      try {
        channel = await events.connect(channelPath);
        await channel.publish({
          chatPk: normalizedChatPk,
          content: text,
          clientMessageId,
        });
      } catch (publishError) {
        setMessages((previousMessages) =>
          previousMessages.map((messageEntry) =>
            messageEntry?.localId === optimisticMessage.localId
              ? { ...messageEntry, isOptimistic: false, deliveryStatus: "failed" }
              : messageEntry
          )
        );
        console.error("Failed to publish message from past chat:", publishError);
        return;
      } finally {
        try {
          channel?.close?.();
        } catch {}
      }

      publishCommunicationEvent({
        type: "chat_message",
        chatPk: normalizedChatPk,
        activityAt,
        preview: text,
      });
      invalidateCommunicationInboxSnapshot(mySenderId);
      communicationCache?.setSnapshot?.("active", mySenderId, {
        availableChats: upsertChatByActivity(cachedActiveChats, promotedChat),
        pendingRequests: cachedPendingRequests.filter(
          (chatEntry) => String(chatEntry?.chatPk || "").trim() !== normalizedChatPk
        ),
        latestSyncedAt: activityAt,
      });
      setAvailableChats((previousChats) =>
        previousChats.filter((chatEntry) => String(chatEntry?.chatPk || "").trim() !== normalizedChatPk)
      );
      navigate(`/active/chat/${encodeURIComponent(chatName)}`, {
        state: {
          selectedChatPk: normalizedChatPk,
          promotedChat,
          pendingOptimisticMessage: optimisticMessage,
        },
      });
    } catch (error) {
      console.error("Failed to send message from past chat:", error);
    }
  }

  const hideConversation = async (chatPk) => {
    const normalizedChatPk = String(chatPk || "").trim();
    if (!normalizedChatPk || !mySenderId) return;

    try {
      await hideConversationForUser({
        userId: mySenderId,
        chatPk: normalizedChatPk,
      });

      setAvailableChats((previousChats) =>
        previousChats.filter((chat) => String(chat?.chatPk || "").trim() !== normalizedChatPk)
      );

      if (String(selectedChatPk || "").trim() === normalizedChatPk) {
        setSelectedChatPk(null);
        setMessages([]);
      }
      publishCommunicationEvent({
        type: "chat_visibility_changed",
        chatPk: normalizedChatPk,
        status: "hidden",
        activityAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Failed to hide conversation:", error);
    }
  };

  const hideMessage = async ({ chatPk, messageTimestamp } = {}) => {
    const normalizedChatPk = String(chatPk || "").trim();
    const normalizedMessageSk = String(messageTimestamp || "").trim();
    if (!normalizedMessageSk || !normalizedChatPk || !mySenderId) return false;

    try {
      await hideMessageForUser({
        userId: mySenderId,
        chatPk: normalizedChatPk,
        messageSk: normalizedMessageSk,
      });

      if (String(selectedChatPk || "").trim() === normalizedChatPk) {
        setMessages((previousMessages) =>
          previousMessages.filter(
            (messageEntry) => String(messageEntry?.timestamp || "") !== normalizedMessageSk
          )
        );
      }
      return true;
    } catch (error) {
      console.error("Failed to hide message:", error);
      return false;
    }
  };

  return {
    me,
    message,
    setMessage,
    input: message,
    setInput: setMessage,
    sendMessage,
    selectedChatPk,
    setSelectedChatPk,
    currentChat,
    selectedChat: currentChat,
    setSelectedChat: (chat) => setSelectedChatPk(chat?.chatPk),
    chatListLoading,
    availableChats,
    chats: availableChats,
    messagesLoading,
    messages,
    hideConversation,
    hideMessage,
  };
}

export const PastContext = createContext(null);

export function PastProvider({ children, outletContextOverride = null }) {
  const value = usePastData(outletContextOverride);
  return (
    <PastContext.Provider value={value}>
      {children}
    </PastContext.Provider>
  );
}

export function usePast() {
  const ctx = useContext(PastContext);
  if (!ctx) {
    throw new Error("usePast must be used within PastProvider");
  }
  return ctx;
}
